import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useRouter, useRoute } from 'vue-router';

import { useUserStore } from './user';

import dayjs, { Dayjs } from 'dayjs';

import { getEvents, getEventLists } from '@/utils/api';

import type { EventType, EventIndexQuery, EventList } from '@/types/Event';
import { getParamsOnLoad } from '@/utils/storeMixin';

export const useEventsStore = defineStore('events', () => {
  const userStore = useUserStore();
  const router = useRouter();
  const route = useRoute();

  const eventsData = ref<EventType[]>([]);
  const lastPage = ref(0);
  const lastUpdated = ref<Dayjs>();
  const loading = ref(false);

  const eventList = ref<EventList>();

  const params = ref<EventIndexQuery>({
    page: 1,
    results_per_page: 10,
    search: '',
    sortDesc: true,
    sortBy: 'event_start',
  });

  function getData(override = false, ignoreLoad = false) {
    // Skip the load, used for when we simply update
    // the list behind the scenes
    if (!ignoreLoad) {
      loading.value = true;
    }

    // Use the Store Cache if we have it.
    if (override === false && eventsData.value.length) {
      loading.value = false;
      return eventsData.value;
    }

    eventsData.value = [];

    const newParams = { ...params.value };

    if (newParams.account === -1) {
      delete newParams.account;
    }

    newParams.event_name = newParams.search;

    getEvents(userStore.accessToken, newParams).then((response) => {
      const { data, last_page } = response.events;

      eventsData.value = data;
      lastPage.value = last_page;
      lastUpdated.value = dayjs();
      loading.value = false;
    });
  }

  function getQueryParamsOnLoad() {
    if (route.path === '/events') {
      params.value = getParamsOnLoad(params.value);
      getData();
    }
  }

  getQueryParamsOnLoad();

  function getEventListData() {
    if (eventList.value) {
      return eventList.value;
    }

    return getEventLists(userStore.accessToken)
      .then((resp) => {
        eventList.value = resp;
        return eventList.value;
      })
      .catch((err) => {
        if (err.status === 401) {
          router.push('/login');
        }
      });
  }

  return {
    eventsData,
    lastPage,
    params,
    loading,

    eventList,

    getData,
    getEventListData,
  };
});
